import FETCHRAW from "@/api/fetchRaw";
import type { FetchRawResponse } from "@/api/fetchRaw";
import { backendPublicPath } from "@/api/servicePaths";
import type { TenantOperationalProfile } from "@/components/features/operator/adminTenantTypes";
import { persistSuperTokensSessionFromResponse } from "@/lib/auth/persistSuperTokensSession";
import { TtlPromiseCache } from "@/lib/cache/ttlPromiseCache";

export type AccountWorkspaceMembershipStatus = "invited" | "active" | "suspended" | "left" | string;
export type AccountWorkspaceRole = "owner" | "admin" | "member" | string;

export interface AccountWorkspace {
  workspace_id: string;
  tenant_guid: string;
  tenant_name?: string;
  display_string: string;
  membership_status: AccountWorkspaceMembershipStatus;
  workspace_role: AccountWorkspaceRole;
  user_guid: string;
  active: boolean;
  onboarding_status?: string;
  lifecycle_state?: string;
}

export interface AccountWorkspaceCapabilities {
  owned_count: number;
  owned_limit: number | null;
  can_create: boolean;
  create_block_reason: string | null;
  joined_count: number;
  joined_limit: number | null;
  can_join: boolean;
  join_block_reason: string | null;
  total_accessible_count: number;
}

export interface AccountApplicationScope {
  scope_type: string;
  scope_id: string;
  allowed_subtree: string[];
}

export interface AccountApplicationContext {
  organization_id: string;
  organization_key: string;
  application_key: string;
  app_installation_id: string;
  app_workspace_id: string;
  app_workspace_key: string;
  actor_user_id: string;
  acting_mode: "self" | "on_behalf" | string;
  acting_as_id: string | null;
  source_app_workspace_id: string | null;
  delegated_access_grant_id: string | null;
  scope: AccountApplicationScope;
  resolved_at: string;
}

export interface AccountWorkspaceBootstrapResponse {
  workspaces: AccountWorkspace[];
  account_guid: string;
  active_workspace_id: string;
  pending_workspace_selection: boolean;
  capabilities: AccountWorkspaceCapabilities;
  application_context: AccountApplicationContext | null;
}

export interface AccountWorkspaceCreateRequest {
  displayString: string;
  idempotencyKey?: string;
}

export interface AccountWorkspaceCreateResult {
  created: boolean;
  workspace: AccountWorkspace;
  provisioning_run_id?: string | null;
  provisioning_status?: string | null;
}

export interface AccountWorkspaceActivationResult {
  activated: boolean;
  workspace: AccountWorkspace;
}

export interface WorkspaceInvitationAcceptResult {
  accepted: boolean;
  tenant_guid: string;
  tenant_name?: string;
  tenant_display_string?: string;
  user_guid: string;
  created_membership: boolean;
  created_usage: boolean;
  already_accepted: boolean;
}

export interface WorkspaceInvitationPreviewWorkspace {
  workspace_id: string;
  display_string: string;
}

export interface WorkspaceInvitationPreviewResult {
  valid: boolean;
  already_joined: boolean;
  workspace?: WorkspaceInvitationPreviewWorkspace | null;
  can_join: boolean;
  join_block_reason?: string | null;
}

const BASE_PATH = backendPublicPath("/account");
const workspacesCache = new TtlPromiseCache<AccountWorkspace[]>(5_000);
const currentWorkspacesBootstrapCache = new TtlPromiseCache<AccountWorkspaceBootstrapResponse>(5_000);
const currentWorkspaceOperationalProfileCache = new TtlPromiseCache<TenantOperationalProfile | null>(5_000);
const workspaceInvitationPreviewCache = new TtlPromiseCache<WorkspaceInvitationPreviewResult>(5_000);
const WORKSPACES_CACHE_KEY = "account-workspaces";
const CURRENT_WORKSPACES_BOOTSTRAP_CACHE_KEY = "account-current-workspaces-bootstrap";
const CURRENT_WORKSPACE_OPERATIONAL_PROFILE_CACHE_KEY = "account-current-workspace-operational-profile";
const WORKSPACE_INVITATION_PREVIEW_CACHE_KEY_PREFIX = "account-workspace-invitation-preview:";
const DEFAULT_WORKSPACE_CAPABILITIES: AccountWorkspaceCapabilities = {
  owned_count: 0,
  owned_limit: null,
  can_create: true,
  create_block_reason: null,
  joined_count: 0,
  joined_limit: null,
  can_join: true,
  join_block_reason: null,
  total_accessible_count: 0
};

const normalizeApplicationContext = (value: unknown): AccountApplicationContext | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const context = value as Partial<AccountApplicationContext>;
  const scope = context.scope && typeof context.scope === "object" ? context.scope : null;
  if (!scope?.scope_id || !scope?.scope_type) {
    return null;
  }

  return {
    organization_id: String(context.organization_id || ""),
    organization_key: String(context.organization_key || ""),
    application_key: String(context.application_key || ""),
    app_installation_id: String(context.app_installation_id || ""),
    app_workspace_id: String(context.app_workspace_id || ""),
    app_workspace_key: String(context.app_workspace_key || ""),
    actor_user_id: String(context.actor_user_id || ""),
    acting_mode: String(context.acting_mode || "self"),
    acting_as_id: context.acting_as_id ? String(context.acting_as_id) : null,
    source_app_workspace_id: context.source_app_workspace_id ? String(context.source_app_workspace_id) : null,
    delegated_access_grant_id: context.delegated_access_grant_id ? String(context.delegated_access_grant_id) : null,
    scope: {
      scope_type: String(scope.scope_type || ""),
      scope_id: String(scope.scope_id || ""),
      allowed_subtree: Array.isArray(scope.allowed_subtree) ? scope.allowed_subtree.map((item) => String(item)) : []
    },
    resolved_at: String(context.resolved_at || "")
  };
};

export interface ListAccountWorkspacesOptions {
  force?: boolean;
  signal?: AbortSignal;
}

const unwrapContent = <T>(response: FetchRawResponse | any): T => {
  const content = response?.content;
  if (content && typeof content === "object" && "content" in content) {
    return content.content as T;
  }
  return content as T;
};

const persistWorkspaceSessionFromResponse = async (response: FetchRawResponse, source: string): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  await persistSuperTokensSessionFromResponse({
    headers: response.headers,
    source,
    status: response.status,
    requireRefreshToken: false
  });
};

const clearWorkspaceCacheAfter = async <T>(operation: Promise<T>): Promise<T> => {
  const result = await operation;
  clearAccountWorkspacesCache();
  return result;
};

export const clearAccountWorkspacesCache = (): void => {
  workspacesCache.clear();
  currentWorkspacesBootstrapCache.clear();
  currentWorkspaceOperationalProfileCache.clear();
  workspaceInvitationPreviewCache.clear();
};

export const listAccountWorkspaces = async (options?: ListAccountWorkspacesOptions): Promise<AccountWorkspace[]> => {
  const fetcher = async () => {
    const response = await FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/`,
      method: "GET",
      signal: options?.signal
    });
    const content = unwrapContent<{ workspaces?: AccountWorkspace[] }>(response);
    return Array.isArray(content?.workspaces) ? content.workspaces : [];
  };

  return options?.force
    ? workspacesCache.getFresh(WORKSPACES_CACHE_KEY, fetcher)
    : workspacesCache.get(WORKSPACES_CACHE_KEY, fetcher);
};

export const listCurrentAccountWorkspaces = async (
  options?: ListAccountWorkspacesOptions
): Promise<AccountWorkspace[]> => {
  const content = await getCurrentAccountWorkspacesBootstrap(options);
  return Array.isArray(content?.workspaces) ? content.workspaces : [];
};

export const getCurrentAccountWorkspacesBootstrap = async (
  options?: ListAccountWorkspacesOptions
): Promise<AccountWorkspaceBootstrapResponse> => {
  const fetcher = async () => {
    const response = await FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/current/`,
      method: "GET",
      signal: options?.signal
    });
    const content = unwrapContent<AccountWorkspaceBootstrapResponse>(response);
    return {
      workspaces: Array.isArray(content?.workspaces) ? content.workspaces : [],
      account_guid: String(content?.account_guid || ""),
      active_workspace_id: String(content?.active_workspace_id || ""),
      pending_workspace_selection: Boolean(content?.pending_workspace_selection),
      capabilities: content?.capabilities || DEFAULT_WORKSPACE_CAPABILITIES,
      application_context: normalizeApplicationContext(content?.application_context)
    };
  };

  return options?.force
    ? currentWorkspacesBootstrapCache.getFresh(CURRENT_WORKSPACES_BOOTSTRAP_CACHE_KEY, fetcher)
    : currentWorkspacesBootstrapCache.get(CURRENT_WORKSPACES_BOOTSTRAP_CACHE_KEY, fetcher);
};

export const getCurrentAccountOperationalProfile = async (
  options?: ListAccountWorkspacesOptions
): Promise<TenantOperationalProfile | null> => {
  const fetcher = async () => {
    const response = await FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/current/operational-profile/`,
      method: "GET",
      signal: options?.signal
    });
    const content = unwrapContent<{ operational_profile?: TenantOperationalProfile }>(response);
    return content?.operational_profile || null;
  };

  return options?.force
    ? currentWorkspaceOperationalProfileCache.getFresh(CURRENT_WORKSPACE_OPERATIONAL_PROFILE_CACHE_KEY, fetcher)
    : currentWorkspaceOperationalProfileCache.get(CURRENT_WORKSPACE_OPERATIONAL_PROFILE_CACHE_KEY, fetcher);
};

export const createAccountWorkspace = async (
  request: AccountWorkspaceCreateRequest
): Promise<AccountWorkspaceCreateResult> => {
  return clearWorkspaceCacheAfter(
    FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/`,
      method: "POST",
      body: JSON.stringify({
        display_string: request.displayString,
        idempotency_key: request.idempotencyKey || ""
      })
    }).then((response) => unwrapContent<AccountWorkspaceCreateResult>(response))
  );
};

export const activateAccountWorkspace = async (workspaceId: string): Promise<AccountWorkspaceActivationResult> => {
  return clearWorkspaceCacheAfter(
    FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/activate/`,
      method: "POST",
      body: JSON.stringify({ workspace_id: workspaceId })
    }).then(async (response) => {
      await persistWorkspaceSessionFromResponse(response, "accountWorkspaceActivation");
      return unwrapContent<AccountWorkspaceActivationResult>(response);
    })
  );
};

export const renameAccountWorkspace = async (workspaceId: string, displayString: string): Promise<AccountWorkspace> => {
  return clearWorkspaceCacheAfter(
    FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspaces/${workspaceId}/`,
      method: "PATCH",
      body: JSON.stringify({ display_string: displayString })
    }).then((response) => unwrapContent<{ workspace: AccountWorkspace }>(response).workspace)
  );
};

export const acceptAccountWorkspaceInvitation = async (
  invitationCode: string
): Promise<WorkspaceInvitationAcceptResult> => {
  return clearWorkspaceCacheAfter(
    FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspace-invitations/accept/`,
      method: "POST",
      body: JSON.stringify({ invitation_code: invitationCode })
    }).then(async (response) => {
      await persistWorkspaceSessionFromResponse(response, "accountWorkspaceInvitationAccept");
      return unwrapContent<WorkspaceInvitationAcceptResult>(response);
    })
  );
};

export const previewAccountWorkspaceInvitation = async (
  invitationCode: string,
  options?: { force?: boolean; signal?: AbortSignal }
): Promise<WorkspaceInvitationPreviewResult> => {
  const code = String(invitationCode || "").trim();
  const fetcher = async () => {
    const response = await FETCHRAW({
      type: "apiurl",
      path: `${BASE_PATH}/workspace-invitations/preview/`,
      method: "POST",
      signal: options?.signal,
      body: JSON.stringify({ invitation_code: code })
    });
    return unwrapContent<WorkspaceInvitationPreviewResult>(response);
  };

  if (!code) {
    return {
      valid: false,
      already_joined: false,
      workspace: null,
      can_join: false,
      join_block_reason: "missing_invitation_code"
    };
  }

  const cacheKey = `${WORKSPACE_INVITATION_PREVIEW_CACHE_KEY_PREFIX}${code}`;
  if (options?.force) {
    return fetcher().then((content) => ({
      valid: Boolean(content?.valid),
      already_joined: Boolean(content?.already_joined),
      workspace: content?.workspace || null,
      can_join: Boolean(content?.can_join),
      join_block_reason: content?.join_block_reason || null
    }));
  }

  return workspaceInvitationPreviewCache.get(cacheKey, fetcher).then((content) => ({
    valid: Boolean(content?.valid),
    already_joined: Boolean(content?.already_joined),
    workspace: content?.workspace || null,
    can_join: Boolean(content?.can_join),
    join_block_reason: content?.join_block_reason || null
  }));
};
